import { FolderOpen, PenSquare, Share2, Newspaper, Mail, Calendar, Rows3, CalendarClock, Settings, BarChart3, LogOut, Users, Megaphone, Star, ClipboardList, UserPlus, FileCheck, PackageCheck, Gift, Landmark, MessageSquareText, Crosshair, UserMinus, Eye, LayoutDashboard, Boxes, Award, MessagesSquare, Globe, Building2, FileSignature, Package } from 'lucide-react';

// Grouped nav (2026-07-27 consolidation). Content Engine + Campaign Planner retired
// from the UI — their data stays in the DB; HG Templates / Newsletter Archive now
// live inside Newsletter Studio. testids unchanged for every surviving item.
const navSections = [
  { header: 'Home', items: [
    { name: 'Dashboard', icon: LayoutDashboard, id: 'dashboard', testid: 'nav-dashboard' },
  ]},
  { header: 'Create', items: [
    { name: 'Social Studio', icon: Share2, id: 'social', testid: 'nav-social' },
    { name: 'Newsletter Studio', icon: Newspaper, id: 'newsletter', testid: 'nav-newsletter' },
    { name: 'Email Studio', icon: Mail, id: 'email', testid: 'nav-email' },
  ]},
  { header: 'Audience', items: [
    { name: 'Subscribers', icon: Users, id: 'subscribers', testid: 'nav-subscribers' },
    { name: 'Customer Replies', icon: MessageSquareText, id: 'customer-replies', testid: 'nav-customer-replies' },
    { name: 'Reviews', icon: Star, id: 'reviews', testid: 'nav-reviews' },
    { name: 'Referrals', icon: Gift, id: 'referrals', testid: 'nav-referrals' },
    { name: 'Survey Results', icon: MessagesSquare, id: 'survey-results', testid: 'nav-survey-results' },
    { name: 'Proof Bank', icon: Award, id: 'proof-bank', testid: 'nav-proof-bank' },
  ]},
  { header: 'Fitters', items: [
    { name: 'Promote Fitters', icon: Megaphone, id: 'promote', testid: 'nav-promote' },
    { name: 'Fitter Packs', icon: Package, id: 'fitter-packs', testid: 'nav-fitter-packs' },
    // Flag-gated. `flag` is resolved in the filter below; an item whose flag is off is
    // not rendered at all, rather than rendering and 404ing.
    { name: 'Lead Files', icon: FolderOpen, id: 'lead-files', testid: 'nav-lead-files', flag: 'leadFiles' },
    { name: 'Compose', icon: PenSquare, id: 'compose', testid: 'nav-compose', flag: 'compose' },
    { name: 'Leads Tracker', icon: Crosshair, id: 'leads-tracker', testid: 'nav-leads-tracker' },
    { name: 'Drip Schedule', icon: CalendarClock, id: 'drip-schedule', testid: 'nav-drip-schedule' },
    { name: 'Facility Partners', icon: Building2, id: 'partners', testid: 'nav-partners' },
    { name: 'LPGA Applications', icon: Award, id: 'lpga-applications', testid: 'nav-lpga-applications' },
    { name: 'Facility Onboarding', icon: FileSignature, id: 'facility-onboarding', testid: 'nav-facility-onboarding' },
    { name: 'Fitter Check-in', icon: ClipboardList, id: 'fitter-checkin', testid: 'nav-fitter-checkin' },
    { name: 'Onboard Fitter', icon: UserPlus, id: 'onboard', testid: 'nav-onboard' },
    { name: 'Offboarding', icon: UserMinus, id: 'offboarding', testid: 'nav-offboarding' },
    { name: 'Approvals', icon: FileCheck, id: 'approvals', testid: 'nav-approvals' },
    { name: 'Returns', icon: PackageCheck, id: 'returns', testid: 'nav-returns' },
    { name: 'Systems Watchlist', icon: Eye, id: 'watchlist', testid: 'nav-watchlist' },
  ]},
  { header: 'Operations', items: [
    { name: 'Inventory', icon: Boxes, id: 'inventory', testid: 'nav-inventory' },
  ]},
  { header: 'Insights', items: [
    { name: 'Planning', icon: Rows3, id: 'planning', testid: 'nav-planning' },
    { name: 'Content Calendar', icon: Calendar, id: 'calendar', testid: 'nav-calendar' },
    { name: 'Analytics', icon: BarChart3, id: 'analytics', testid: 'nav-analytics' },
    { name: 'Google', icon: Globe, id: 'google', testid: 'nav-google' },
  ]},
  { header: 'Admin', items: [
    { name: 'Settings', icon: Settings, id: 'settings', testid: 'nav-settings' },
  ]},
];

const HGLogo = () => (
  <div className="flex items-center gap-3 px-5 py-6 border-b border-hg-border">
    <div
      className="w-10 h-10 flex items-center justify-center font-display font-bold text-sm text-hg-text rounded-sm flex-shrink-0"
      style={{ background: 'linear-gradient(135deg, #5EC862 0%, #48B64C 50%, #3A9A3E 100%)' }}
    >
      HG
    </div>
    <div className="min-w-0">
      <div className="font-display text-[15px] text-hg-text leading-tight tracking-wider">HUB</div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-hg-text-dim mt-0.5 font-sans">Henry-Griffitts</div>
    </div>
  </div>
);

export default function Sidebar({ activeModule, onModuleChange, user, onLogout, showCfo = false,
                                 showLeadFiles = false, showCompose = false }) {
  // CFO is allowlist-gated (server-verified via /api/cfo/access) — the item only exists
  // for allowed users. Hiding it here is cosmetic; the API enforces access regardless.
  const flags = { leadFiles: showLeadFiles, compose: showCompose };
  const sections = navSections
    .map((s) =>
      s.header === 'Insights' && showCfo
        ? { ...s, items: [...s.items, { name: 'CFO', icon: Landmark, id: 'cfo', testid: 'nav-cfo' }] }
        : s)
    // A flagged item renders only when its flag is on. Everything unflagged is unaffected.
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.flag || flags[i.flag]) }))
    .filter((s) => s.items.length);
  return (
    <aside data-testid="sidebar" className="w-64 flex-shrink-0 border-r border-hg-border bg-hg-bg h-screen sticky top-0 flex flex-col">
      <HGLogo />
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {sections.map((section) => (
          <div key={section.header} className="mb-3">
            <div className="px-3 pb-1.5 text-[9px] font-sans uppercase tracking-[0.25em] text-hg-text-dim/70">
              {section.header}
            </div>
            <div className="space-y-1">
              {section.items.map((item) => {
                const isActive = activeModule === item.id;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    data-testid={item.testid}
                    onClick={() => onModuleChange(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-sans transition-colors ${
                      isActive
                        ? 'bg-hg-green/10 text-hg-green border border-hg-green/20'
                        : 'text-hg-text-dim hover:text-hg-text hover:bg-hg-surface border border-transparent'
                    }`}
                  >
                    <Icon size={18} strokeWidth={1.5} />
                    <span>{item.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      {user && (
        <div className="px-3 py-3 border-t border-hg-border">
          <div className="flex items-center gap-3 px-2 py-2">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-sm border border-hg-border" />
            ) : (
              <div className="w-7 h-7 rounded-sm bg-hg-green/10 border border-hg-green/30 flex items-center justify-center text-[10px] font-sans text-hg-green font-medium">
                {(user.name || user.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-sans text-hg-text truncate">{user.name || 'Signed in'}</div>
              <div className="text-[10px] font-sans text-hg-text-dim truncate">{user.email}</div>
            </div>
          </div>
          <button
            data-testid="sidebar-logout-btn"
            onClick={onLogout}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-sans text-hg-text-dim hover:text-hg-green hover:bg-hg-surface transition-colors"
          >
            <LogOut size={13} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      )}
      <div className="px-5 py-3 border-t border-hg-border">
        <div className="text-[10px] text-hg-text-dim uppercase tracking-wider font-sans">v2.1 — Private</div>
      </div>
    </aside>
  );
}
